import React, { useState, useRef, useEffect, useLayoutEffect } from 'react'
import ReactDOM from 'react-dom'
import { Edit2, Check, X } from 'lucide-react'
import {
  DayCalculation,
  LOSCategory,
  PAXCategory,
  DayPriceOverride,
  OTAChannel,
  BusinessModel,
} from '../types'
import type { CSSProperties } from 'react'

interface PricingMatrixProps {
  calculation: DayCalculation
  selectedChannel: string
  losCategories: LOSCategory[]
  paxCategories: PAXCategory[]
  otaChannels: OTAChannel[]
  compact?: boolean
  onPriceOverride?: (
    date: string,
    price: number,
    losId: string,
    paxId: string,
  ) => void
  onChannelSelect?: (channelId: string) => void
  selectedDate?: Date
  priceOverrides?: DayPriceOverride[]
  basePrice: number
  businessModel: BusinessModel
}

// Add this type for live calculation rows
type TooltipRow = {
  label: string
  percent?: string
  amount?: string
  subtotal: string
}

const PricingMatrix: React.FC<PricingMatrixProps> = ({
  calculation,
  selectedChannel,
  losCategories,
  paxCategories,
  otaChannels,
  compact = false,
  onPriceOverride,
  onChannelSelect,
  selectedDate,
  priceOverrides = [],
  basePrice,
  businessModel,
}) => {
  const [editingCell, setEditingCell] = useState<string | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [hoveredCell, setHoveredCell] = useState<string | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{
    top: number
    left: number
  } | null>(null)
  const [tooltipHeight, setTooltipHeight] = useState<number>(0)
  const cellRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const tooltipRef = useRef<HTMLDivElement | null>(null)

  const channelPrices = calculation.prices[selectedChannel]

  const enabledChannels = otaChannels.filter((channel) => channel.enabled)

  if (!channelPrices) {
    return (
      <div className="text-center text-gray-500 py-4">
        No pricing data available for selected channel
      </div>
    )
  }

  const isOverridden = (losId: string, paxId: string): boolean => {
    return priceOverrides.some(
      (override) =>
        override.date === calculation.date &&
        override.losId === losId &&
        override.paxId === paxId,
    )
  }

  const startEdit = (losId: string, paxId: string) => {
    if (compact || !onPriceOverride) return

    const cellKey = `${losId}-${paxId}`
    const price = channelPrices[losId]?.[paxId]
    setHoveredCell(null) // Clear any hover state when starting to edit
    setEditingCell(cellKey)
    setEditValue(price?.basePrice.toString() || '0')
  }

  const commitEdit = () => {
    if (!onPriceOverride || !selectedDate || editingCell === null) return

    const newPrice = parseFloat(editValue)
    if (!isNaN(newPrice) && newPrice > 0) {
      const [losId, paxId] = editingCell.split('-')
      const originalPrice = channelPrices[losId]?.[paxId]?.basePrice

      // Only call onPriceOverride if the price has actually changed
      if (originalPrice !== newPrice) {
        onPriceOverride(calculation.date, newPrice, losId, paxId)
      }
    }
    setEditingCell(null)
    setEditValue('')
    setTooltipPos(null) // Clear tooltip when edit finishes
  }

  const getCellStyle = (losId: string, paxId: string) => {
    const price = channelPrices[losId]?.[paxId]
    if (!price) return 'bg-gray-100 text-gray-400 border-gray-200'

    // Find the default LOS and PAX
    const defaultLos = losCategories.find((los) => los.isDefault)
    const defaultPax = paxCategories.find((pax) => pax.isDefault)
    const isDefaultPrice = losId === defaultLos?.id && paxId === defaultPax?.id
    const defaultStyle = isDefaultPrice ? 'ring-2 ring-primary-500' : ''

    if (!price.isValid) {
      return `bg-danger-50 text-danger-700 border-danger-200 hover:bg-danger-100 ${defaultStyle}`
    } else if (price.exceedsMax) {
      return `bg-warning-50 text-warning-700 border-warning-200 hover:bg-warning-100 ${defaultStyle}`
    } else if (price.belowMin) {
      return `bg-warning-50 text-warning-700 border-warning-200 hover:bg-warning-100 ${defaultStyle}`
    }

    return `bg-success-50 text-success-700 border-success-200 hover:bg-success-100 ${defaultStyle}`
  }

  const renderCalculationTooltip = (
    price: {
      basePrice: number
      finalPrice: number
      appliedRules: string[]
      losModifier?: { name: string; percent: number }
      paxModifier?: { name: string; percent: number }
      ruleBreakdown?: { name: string; percent: number }[]
      channelMargin?: { name: string; percent: number }
      preChannelFeePrice?: number
    },
    losId: string,
    paxId: string,
  ) => {
    const losCategory = losCategories.find((los) => los.id === losId)
    const paxCategory = paxCategories.find((pax) => pax.id === paxId)
    let rows: {
      label: string
      percent?: string
      amount?: string
      subtotal: string
    }[] = []

    // Calculate override percentage if this price is overridden
    const isOverriddenPrice = isOverridden(losId, paxId)
    let overridePercentage = ''
    if (isOverriddenPrice) {
      const defaultLos = losCategories.find((los) => los.priceModifier === 0)
      const defaultPax = paxCategories.find(
        (pax) => pax.sellingPriceDifference === 0,
      )
      const defaultPrice =
        channelPrices[defaultLos?.id || '']?.[defaultPax?.id || '']
          ?.finalPrice || 0
      if (defaultPrice > 0) {
        const percentageDiff =
          ((price.finalPrice - defaultPrice) / defaultPrice) * 100
        overridePercentage = `${
          percentageDiff > 0 ? '+' : ''
        }${percentageDiff.toFixed(1)}%`
      }
    }

    let current = price.basePrice
    rows.push({
      label: 'Base Price',
      subtotal: `€${basePrice.toFixed(2)}`,
      percent: '',
    })

    // Check for both daily and LOS/PAX specific overrides
    const dailyOverride = priceOverrides.find(
      (o) => o.date === calculation.date && !o.losId && !o.paxId,
    )
    const specificOverride = priceOverrides.find(
      (o) =>
        o.date === calculation.date && o.losId === losId && o.paxId === paxId,
    )

    // Show daily override if it exists
    if (dailyOverride && dailyOverride.basePrice !== basePrice) {
      const percentageDiff =
        ((dailyOverride.basePrice - basePrice) / basePrice) * 100
      const overridePercentage = `${
        percentageDiff > 0 ? '+' : ''
      }${percentageDiff.toFixed(1)}%`
      const deltaAmount = dailyOverride.basePrice - basePrice
      rows.push({
        label: 'Daily Override',
        subtotal: `€${dailyOverride.basePrice.toFixed(2)}`,
        percent: overridePercentage,
        amount: `${deltaAmount >= 0 ? '+' : ''}€${deltaAmount.toFixed(2)}`,
      })
      current = dailyOverride.basePrice
    }

    // Show LOS/PAX specific override if it exists
    if (
      specificOverride &&
      specificOverride.basePrice !== (dailyOverride?.basePrice || basePrice)
    ) {
      const baseForPercentage = dailyOverride?.basePrice || basePrice
      const percentageDiff =
        ((specificOverride.basePrice - baseForPercentage) / baseForPercentage) *
        100
      const overridePercentage = `${
        percentageDiff > 0 ? '+' : ''
      }${percentageDiff.toFixed(1)}%`
      const deltaAmount = specificOverride.basePrice - baseForPercentage
      rows.push({
        label: 'LOS/PAX Override',
        subtotal: `€${specificOverride.basePrice.toFixed(2)}`,
        percent: overridePercentage,
        amount: `${deltaAmount >= 0 ? '+' : ''}€${deltaAmount.toFixed(2)}`,
      })
      current = specificOverride.basePrice
    }

    // LOS modifier
    if (
      losCategory &&
      losCategory.sellingPriceDifference &&
      losCategory.sellingPriceDifference !== 0
    ) {
      let diff = 0
      let percentLabel = ''
      if (losCategory.sellingPriceDifferenceType === 'percent') {
        diff = current * (losCategory.sellingPriceDifference / 100)
        percentLabel = `${losCategory.sellingPriceDifference > 0 ? '+' : ''}${
          losCategory.sellingPriceDifference
        }%`
      } else {
        diff = losCategory.sellingPriceDifference
        percentLabel = `${
          losCategory.sellingPriceDifference > 0 ? '+' : ''
        }€${losCategory.sellingPriceDifference.toFixed(2)}`
      }
      current += diff
      rows.push({
        label: `LOS: ${losCategory.name}`,
        percent: percentLabel,
        amount: `${diff >= 0 ? '+' : ''}€${diff.toFixed(2)}`,
        subtotal: `€${current.toFixed(2)}`,
      })
    }
    // PAX modifier
    if (
      paxCategory &&
      paxCategory.sellingPriceDifference &&
      paxCategory.sellingPriceDifference !== 0
    ) {
      let diff = 0
      let percentLabel = ''
      if (paxCategory.sellingPriceDifferenceType === 'percent') {
        diff = current * (paxCategory.sellingPriceDifference / 100)
        percentLabel = `${paxCategory.sellingPriceDifference > 0 ? '+' : ''}${
          paxCategory.sellingPriceDifference
        }%`
      } else {
        diff = paxCategory.sellingPriceDifference
        percentLabel = `${
          paxCategory.sellingPriceDifference > 0 ? '+' : ''
        }€${paxCategory.sellingPriceDifference.toFixed(2)}`
      }
      current += diff
      rows.push({
        label: `PAX: ${paxCategory.name}`,
        percent: percentLabel,
        amount: `${diff >= 0 ? '+' : ''}€${diff.toFixed(2)}`,
        subtotal: `€${current.toFixed(2)}`,
      })
    }
    // Pricing rules (if available as breakdown)
    if (price.ruleBreakdown && Array.isArray(price.ruleBreakdown)) {
      price.ruleBreakdown.forEach((rule) => {
        // Filter out LOS/PAX label rules
        if (
          (losCategory &&
            rule.name.toLowerCase().includes(losCategory.name.toLowerCase())) ||
          (paxCategory &&
            rule.name.toLowerCase().includes(paxCategory.name.toLowerCase()))
        ) {
          return
        }
        const diff = current * (rule.percent / 100)
        current += diff
        rows.push({
          label: rule.name,
          percent: `${rule.percent > 0 ? '+' : ''}${rule.percent}%`,
          amount: `${diff >= 0 ? '+' : ''}€${diff.toFixed(2)}`,
          subtotal: `€${current.toFixed(2)}`,
        })
      })
    }

    // Store the pre-channel-fee price
    const preChannelFeePrice = price.preChannelFeePrice || current

    // Channel margin (fee) row (always show if marginPercent !== 0)
    const channel = enabledChannels.find((ch) => ch.id === selectedChannel)
    if (
      channel &&
      channel.marginPercent !== 0 &&
      preChannelFeePrice !== undefined
    ) {
      const diff = price.finalPrice - preChannelFeePrice
      current += diff
      rows.push({
        label: `${channel.name} fee`,
        percent: `${channel.marginPercent > 0 ? '+' : ''}${
          channel.marginPercent
        }%`,
        amount: `${diff >= 0 ? '+' : ''}€${diff.toFixed(2)}`,
        subtotal: `€${current.toFixed(2)}`,
      })
    }

    // Final price
    rows.push({
      label: 'Final Price',
      subtotal: `€${price.finalPrice.toFixed(2)}`,
    })

    // Owner/Agency rows (after final price)
    let ownerAgencyRows: typeof rows = []
    const priceForAgencyOwner = preChannelFeePrice
    if (businessModel.type === 'agency') {
      if (
        businessModel.model === 'absolute_same' ||
        businessModel.model === 'absolute_different'
      ) {
        const agencyAmount = businessModel.agencyFee || 0
        const ownerAmount = priceForAgencyOwner - agencyAmount
        ownerAgencyRows.push({
          label: 'Owner Amount',
          percent: '',
          amount: '',
          subtotal: `€${ownerAmount.toFixed(2)}`,
        })
        ownerAgencyRows.push({
          label: 'Agency Amount',
          percent: '',
          amount: '',
          subtotal: `€${agencyAmount.toFixed(2)}`,
        })
      } else if (businessModel.model === 'relative_percentage') {
        const ownerPercent = businessModel.ownerPayoutPercent || 0
        const ownerAmount = priceForAgencyOwner * (ownerPercent / 100)
        const agencyAmount = priceForAgencyOwner - ownerAmount
        ownerAgencyRows.push({
          label: 'Owner Amount',
          percent: `${ownerPercent}%`,
          amount: '',
          subtotal: `€${ownerAmount.toFixed(2)}`,
        })
        ownerAgencyRows.push({
          label: 'Agency Amount',
          percent: '',
          amount: '',
          subtotal: `€${agencyAmount.toFixed(2)}`,
        })
      }
    } else {
      // Direct model: only show Owner Amount
      ownerAgencyRows.push({
        label: 'Owner Amount',
        percent: '',
        amount: '',
        subtotal: `€${priceForAgencyOwner.toFixed(2)}`,
      })
    }
    // Channel Fee (always show if there is a difference)
    if (preChannelFeePrice !== price.finalPrice) {
      const channelFee = price.finalPrice - preChannelFeePrice
      ownerAgencyRows.push({
        label: 'Channel Fee',
        percent: '',
        amount: '',
        subtotal: `€${channelFee.toFixed(2)}`,
      })
    }

    // Tooltip logic: always show on editing cell if editing, otherwise on hovered cell
    const activeCell = editingCell || hoveredCell
    const shouldShowTooltip = Boolean(activeCell)
    const tooltipStyle: CSSProperties = tooltipPos
      ? {
          position: 'fixed',
          top: tooltipPos.top,
          left: tooltipPos.left,
          zIndex: 9999,
        }
      : { position: 'fixed', top: -9999, left: -9999, zIndex: 9999 }
    return shouldShowTooltip
      ? ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            style={tooltipStyle}
            className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs whitespace-pre font-mono min-w-[380px] max-w-[480px]"
          >
            <div className="flex flex-row justify-between items-baseline font-bold mb-1">
              <span className="text-left w-2/6 truncate" title="Step">
                Step
              </span>
              <span className="text-center w-1/6 px-2">%</span>
              <span className="text-right w-1/6 px-2">Δ</span>
              <span className="text-right w-2/6">Subtotal</span>
            </div>
            {rows.map((row, idx) => (
              <React.Fragment key={idx}>
                {row.label === 'Final Price' && (
                  <div className="border-t border-gray-300 my-1"></div>
                )}
                <div className="flex flex-row justify-between items-baseline">
                  <span className="text-left w-2/6 truncate" title={row.label}>
                    {row.label}
                  </span>
                  <span className="text-center w-1/6 px-2">
                    {row.percent || ''}
                  </span>
                  <span className="text-right w-1/6 px-2">
                    {row.amount || ''}
                  </span>
                  <span className="text-right w-2/6">{row.subtotal}</span>
                </div>
              </React.Fragment>
            ))}
            {ownerAgencyRows.length > 0 && (
              <>
                <div className="border-t border-gray-300 my-1"></div>
                {ownerAgencyRows.map((row, idx) => (
                  <div
                    key={idx}
                    className="flex flex-row justify-between items-baseline"
                  >
                    <span
                      className="text-left w-2/6 truncate"
                      title={row.label}
                    >
                      {row.label}
                    </span>
                    <span className="text-center w-1/6">
                      {row.percent || ''}
                    </span>
                    <span className="text-right w-1/6">{row.amount || ''}</span>
                    <span className="text-right w-2/6">{row.subtotal}</span>
                  </div>
                ))}
              </>
            )}
          </div>,
          document.body,
        )
      : null
  }

  useLayoutEffect(() => {
    // Always use editingCell if present, otherwise hoveredCell
    const key = editingCell || hoveredCell || ''
    const tooltipEl = tooltipRef.current
    const cellEl = cellRefs.current[key]
    if ((editingCell || hoveredCell) && tooltipEl && cellEl) {
      requestAnimationFrame(() => {
        const tooltipRect = tooltipEl.getBoundingClientRect()
        const cellRect = cellEl.getBoundingClientRect()
        const margin = 8
        const top = cellRect.top - tooltipRect.height - margin
        const left = cellRect.left + cellRect.width / 2 - tooltipRect.width / 2
        setTooltipHeight(tooltipRect.height)
        setTooltipPos({ top, left })
      })
    } else if (!editingCell && !hoveredCell) {
      setTooltipPos(null)
    }
  }, [editingCell, hoveredCell])

  const renderCell = (losId: string, paxId: string) => {
    const price = channelPrices[losId]?.[paxId]
    const cellKey = `${losId}-${paxId}`
    const isEditing = editingCell === cellKey
    const isHovered = hoveredCell === cellKey && !editingCell

    if (!price) {
      return <div className="text-center text-gray-400 text-xs">N/A</div>
    }

    // Live calculation for editing
    let livePrice = price
    if (isEditing && editValue !== '' && !isNaN(Number(editValue))) {
      const newBase = parseFloat(editValue)
      let current = newBase
      const rows: TooltipRow[] = []

      rows.push({ label: 'Base Price', subtotal: `€${basePrice.toFixed(2)}` })
      const percentageDiff = ((newBase - basePrice) / basePrice) * 100
      const overridePercentage = `${
        percentageDiff > 0 ? '+' : ''
      }${percentageDiff.toFixed(1)}%`
      rows.push({
        label: 'Override Price',
        subtotal: `€${newBase.toFixed(2)}`,
        percent: overridePercentage,
      })

      // Continue with the rest of the calculations using newBase
      if (price.ruleBreakdown && price.ruleBreakdown.length > 0) {
        const losRule = price.ruleBreakdown.find((r) =>
          r.name.startsWith('LOS:'),
        )
        if (losRule) {
          const diff = newBase * (losRule.percent / 100)
          current += diff
          rows.push({
            label: losRule.name.replace('LOS: ', ''),
            percent: `${losRule.percent > 0 ? '+' : ''}${losRule.percent}%`,
            amount: `${diff >= 0 ? '+' : ''}€${diff.toFixed(2)}`,
            subtotal: `€${current.toFixed(2)}`,
          })
        }
        const paxRule = price.ruleBreakdown.find((r) =>
          r.name.startsWith('PAX:'),
        )
        if (paxRule) {
          const diff = current * (paxRule.percent / 100)
          current += diff
          rows.push({
            label: paxRule.name.replace('PAX: ', ''),
            percent: `${paxRule.percent > 0 ? '+' : ''}${paxRule.percent}%`,
            amount: `${diff >= 0 ? '+' : ''}€${diff.toFixed(2)}`,
            subtotal: `€${current.toFixed(2)}`,
          })
        }
        price.ruleBreakdown.forEach((rule) => {
          if (rule.name.startsWith('LOS:') || rule.name.startsWith('PAX:'))
            return
          const diff = current * (rule.percent / 100)
          current += diff
          rows.push({
            label: rule.name,
            percent: `${rule.percent > 0 ? '+' : ''}${rule.percent}%`,
            amount: `${diff >= 0 ? '+' : ''}€${diff.toFixed(2)}`,
            subtotal: `€${current.toFixed(2)}`,
          })
        })
      }
      rows.push({ label: 'Final Price', subtotal: `€${current.toFixed(2)}` })

      livePrice = {
        ...price,
        basePrice: newBase,
        finalPrice: current,
        ruleBreakdown: price.ruleBreakdown,
        _liveRows: rows,
      } as typeof price & { _liveRows: TooltipRow[] }
    }

    return (
      <div
        className="relative"
        ref={(el) => (cellRefs.current[cellKey] = el)}
        onMouseEnter={() => {
          if (!editingCell) setHoveredCell(cellKey)
        }}
        onMouseLeave={() => {
          if (!editingCell) setHoveredCell(null)
        }}
      >
        {isEditing ? (
          <div className="flex items-center gap-1">
            <div className="relative flex-1">
              <input
                type="number"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={(e) => {
                  // Don't commit if clicking the reset button
                  if (e.relatedTarget?.tagName === 'BUTTON') return
                  commitEdit()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    commitEdit()
                  } else if (e.key === 'Escape') {
                    setEditingCell(null)
                    setEditValue('')
                    setTooltipPos(null)
                  }
                }}
                className="w-full text-sm font-medium text-gray-900 border-gray-300 rounded-md pl-8 pr-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-400 text-right transition-all duration-150 hover:border-gray-400 focus:border-primary-400"
                style={{
                  minWidth: 70,
                  maxWidth: 100,
                  margin: '0 auto',
                  display: 'block',
                }}
                autoFocus
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                €
              </span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation()
                const [losId, paxId] = editingCell!.split('-')
                // Remove the override for this cell
                if (onPriceOverride) {
                  onPriceOverride(calculation.date, -1, losId, paxId)
                }
                setEditingCell(null)
                setEditValue('')
                setTooltipPos(null)
              }}
              className="ml-1 px-1 py-0.5 text-xs bg-gray-200 rounded hover:bg-gray-300"
            >
              Reset
            </button>
          </div>
        ) : (
          <div
            className={`text-center cursor-pointer group transition-all relative h-[32px] flex items-center justify-center ${
              compact ? 'text-xs' : 'text-sm'
            }`}
            onClick={() => startEdit(losId, paxId)}
          >
            <div className="font-medium text-sm">
              €{price.finalPrice.toFixed(2)}
            </div>
            {!compact && isOverridden(losId, paxId) && (
              <div
                className="override-indicator"
                title="Price overridden"
              ></div>
            )}
          </div>
        )}
        {(isEditing || isHovered) &&
          renderCalculationTooltip(livePrice, losId, paxId)}
      </div>
    )
  }

  if (compact) {
    // Simplified grid for calendar view
    return (
      <div
        className={`los-pax-matrix grid-cols-${Math.min(
          paxCategories.length,
          3,
        )}`}
      >
        {losCategories.slice(0, 2).map((los) =>
          paxCategories.slice(0, 3).map((pax) => (
            <div
              key={`${los.id}-${pax.id}`}
              className={`p-1 border rounded text-xs ${getCellStyle(
                los.id,
                pax.id,
              )}`}
            >
              {renderCell(los.id, pax.id)}
            </div>
          )),
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Header Row */}
      <div className="flex items-center gap-3">
        <div className="w-36 text-right pr-3">
          <span className="text-sm font-medium text-gray-500">LOS \ PAX</span>
        </div>
        <div
          className="flex-1 grid gap-3"
          style={{
            gridTemplateColumns: `repeat(${paxCategories.length}, minmax(0, 1fr))`,
          }}
        >
          {paxCategories.map((pax) => (
            <div
              key={pax.id}
              className="text-center py-1.5 px-2"
              title={pax.name}
            >
              <div className="text-sm font-medium text-gray-900">
                {pax.persons} person{pax.persons !== 1 ? 's' : ''}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Data Rows */}
      {losCategories.map((los) => (
        <div key={los.id} className="flex items-center gap-3">
          {/* LOS Label - Fixed width to match header */}
          <div className="w-36 text-right pr-3">
            <div
              className="inline-block text-right py-1.5 px-2"
              title={los.name}
            >
              <div className="text-sm font-medium text-gray-900">
                {los.minNights}-{los.maxNights || '∞'} nights
              </div>
            </div>
          </div>

          {/* Price Cells */}
          <div
            className="flex-1 grid gap-3"
            style={{
              gridTemplateColumns: `repeat(${paxCategories.length}, minmax(0, 1fr))`,
            }}
          >
            {paxCategories.map((pax) => (
              <div
                key={pax.id}
                className={`p-2 rounded-lg border transition-all hover:shadow-sm cursor-pointer h-[48px] flex items-center justify-center ${getCellStyle(
                  los.id,
                  pax.id,
                )}`}
                ref={(el) => (cellRefs.current[`${los.id}-${pax.id}`] = el)}
                onMouseEnter={() => {
                  // Only set hovered cell if we're not currently editing
                  if (!editingCell) {
                    setHoveredCell(`${los.id}-${pax.id}`)
                  }
                }}
                onMouseLeave={() => {
                  // Always clear hovered cell when leaving, unless we're editing this specific cell
                  if (editingCell !== `${los.id}-${pax.id}`) {
                    setHoveredCell(null)
                  }
                }}
              >
                {renderCell(los.id, pax.id)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default PricingMatrix
